/**
 * Read-only viewer dialogs for the Cash Flow hub — Access "Need to map" /
 * "Duplicate Data" / "Misa Vender/Customer" / "Misa Invoices" buttons open a
 * subform-hosting popup. On web: one Dialog wrapping the shared list grid
 * (non-selectable). Data comes from the matching `v_report_cashflow_*` views.
 */
import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import { useMemo } from 'react'
import {
  useGetCashflowMapCpNeedQuery,
  useGetCashflowMapCpDupQuery,
  useGetCashflowMisaObjectQuery,
  useGetCashflowMapServiceQuery,
  useGetCashflowMapServiceDupQuery,
  useGetCashflowMapMuaHangNeedQuery,
  useGetCashflowMisaMuaHangQuery,
  useGetCashflowMapBanHangQuery,
  useGetCashflowMisaBanHangQuery,
} from '../../../store/api/reportCashflowApi'
import CashflowDataList, { type CashflowColumn } from './CashflowDataList'
import {
  CP_COLUMNS,
  CP_NEED_COLUMNS,
  SERVICE_COLUMNS,
  MISA_OBJECT_COLUMNS,
  MUAHANG_COLUMNS,
  BANHANG_COLUMNS,
  type CashflowAction,
} from './cashflowPages'

type Row = Record<string, unknown>

const MISA_MUAHANG_COLUMNS: CashflowColumn<Row>[] = [
  { key: 'tabName', label: 'Tab Name' },
  { key: 'sochungtu', label: 'Voucher No' },
  { key: 'ngayhachtoan', label: 'Posting Date', kind: 'date' },
  { key: 'nhacungcap', label: 'Vender' },
  { key: 'sohoadon', label: 'Invoice No' },
  { key: 'ngayhoadon', label: 'Invoice Date', kind: 'date' },
  { key: 'loaitien', label: 'Currency' },
  { key: 'tygia', label: 'Exchange Rate', kind: 'number', align: 'right' },
  { key: 'thanhtienQuydoi', label: 'Amount (Convert)', kind: 'number', align: 'right' },
  { key: 'thanhtoanQuydoi', label: 'Payment (Convert)', kind: 'number', align: 'right' },
  { key: 'journalMemo', label: 'Description' },
  { key: 'refid', label: 'Refid' },
  { key: 'mappedTxt', label: 'Is Mapped?' },
]

const MISA_BANHANG_COLUMNS: CashflowColumn<Row>[] = [
  { key: 'tabName', label: 'Tab Name' },
  { key: 'sochungtu', label: 'Voucher No' },
  { key: 'ngaychungtu', label: 'Voucher Date', kind: 'date' },
  { key: 'erpCpName', label: 'Customer' },
  { key: 'misaSohoadon', label: 'Invoice No' },
  { key: 'loaitien', label: 'Currency' },
  { key: 'tygia', label: 'Exchange Rate', kind: 'number', align: 'right' },
  { key: 'tongTienThanhToan', label: 'Total Payment', kind: 'number', align: 'right' },
  { key: 'dienGiai', label: 'Note' },
  { key: 'misaContract', label: 'Contract' },
  { key: 'refId', label: 'Ref Id' },
  { key: 'isMapped', label: 'Is Mapped?' },
]

const UNMAPPED = new Set(['', 'no', 'chưa', 'chua', 'not mapped', 'unmapped', 'false'])
const isUnmapped = (v: unknown) => UNMAPPED.has(String(v ?? '').trim().toLowerCase())

const TITLES: Record<string, string> = {
  'cp-need': 'CP Mapping - Objects need to map',
  'cp-dup': 'CP Mapping - Duplicate Data',
  'cp-misa': 'CP Mapping - Misa Vender/Customer',
  'svc-need': 'Service Mapping - Objects need to map',
  'svc-dup': 'Service Mapping - Duplicate Data',
  'svc-misa': 'Service Mapping - Misa Vender/Customer',
  'mh-need': 'Purchase Invoice - Need to map',
  'mh-misa': 'Purchase Invoice - Misa Invoices',
  'bh-need': 'Sale Invoice - Need to map',
  'bh-misa': 'Sale Invoice - Misa Invoices',
}

interface Props {
  action: CashflowAction | null
  onClose: () => void
}

export default function CashflowViewerDialog({ action, onClose }: Props) {
  const open = action !== null && action in TITLES

  const cpNeed = useGetCashflowMapCpNeedQuery(undefined, { skip: action !== 'cp-need' })
  const cpDup = useGetCashflowMapCpDupQuery(undefined, { skip: action !== 'cp-dup' })
  const svc = useGetCashflowMapServiceQuery(undefined, { skip: action !== 'svc-need' })
  const svcDup = useGetCashflowMapServiceDupQuery(undefined, { skip: action !== 'svc-dup' })
  const misaObj = useGetCashflowMisaObjectQuery(undefined, { skip: action !== 'cp-misa' && action !== 'svc-misa' })
  const mhNeed = useGetCashflowMapMuaHangNeedQuery(undefined, { skip: action !== 'mh-need' })
  const misaMh = useGetCashflowMisaMuaHangQuery(undefined, { skip: action !== 'mh-misa' })
  const bh = useGetCashflowMapBanHangQuery(undefined, { skip: action !== 'bh-need' })
  const misaBh = useGetCashflowMisaBanHangQuery(undefined, { skip: action !== 'bh-misa' })

  const cfg = useMemo(() => {
    switch (action) {
      case 'cp-need':
        return { q: cpNeed, columns: CP_NEED_COLUMNS, rows: cpNeed.data?.items ?? [], id: (r: Row) => String(r.erpCode ?? '') }
      case 'cp-dup':
        return { q: cpDup, columns: CP_COLUMNS, rows: cpDup.data?.items ?? [], id: (r: Row) => String(r.erpCode ?? '') }
      case 'cp-misa':
      case 'svc-misa':
        return { q: misaObj, columns: MISA_OBJECT_COLUMNS, rows: misaObj.data?.items ?? [], id: (r: Row) => String(r.misaId ?? '') }
      case 'svc-need':
        return {
          q: svc,
          columns: SERVICE_COLUMNS,
          rows: (svc.data?.items ?? []).filter((r) => isUnmapped((r as unknown as Row).isMapped)),
          id: (r: Row) => String(r.serviceCode ?? ''),
        }
      case 'svc-dup':
        return { q: svcDup, columns: SERVICE_COLUMNS, rows: svcDup.data?.items ?? [], id: (r: Row) => String(r.serviceCode ?? '') }
      case 'mh-need':
        return { q: mhNeed, columns: MUAHANG_COLUMNS, rows: mhNeed.data?.items ?? [], id: (r: Row) => String(r.erpInvNo ?? '') }
      case 'mh-misa':
        return { q: misaMh, columns: MISA_MUAHANG_COLUMNS, rows: misaMh.data?.items ?? [], id: (r: Row) => String(r.refid ?? '') }
      case 'bh-need':
        return {
          q: bh,
          columns: BANHANG_COLUMNS,
          rows: (bh.data?.items ?? []).filter((r) => isUnmapped((r as unknown as Row).isMapped)),
          id: (r: Row) => String(r.contractNo ?? ''),
        }
      case 'bh-misa':
        return { q: misaBh, columns: MISA_BANHANG_COLUMNS, rows: misaBh.data?.items ?? [], id: (r: Row) => String(r.refId ?? '') }
      default:
        return null
    }
  }, [action, cpNeed, cpDup, misaObj, svc, svcDup, mhNeed, misaMh, bh, misaBh])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '88vh' } }}>
      <DialogTitle sx={{ py: 1, pr: 6 }}>
        <Typography component="span" variant="subtitle1" fontWeight={700}>
          {action ? TITLES[action] : ''}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
        {cfg && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <CashflowDataList
              columns={cfg.columns as CashflowColumn<Row>[]}
              rows={cfg.rows as Row[]}
              getRowId={cfg.id}
              loading={cfg.q.isLoading}
              fetching={cfg.q.isFetching}
              error={!!cfg.q.error}
              errorTitle="Could not load data"
              emptyTitle="No records found"
              searchPlaceholder="Search…"
              selectable={false}
              selectedId={null}
              onSelect={() => {}}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
